
import { Order, Driver, ServiceTimeResolution } from '../../../types';
import { calculateServiceTimeMetrics, resolveServiceLearningDriverId } from './telemetry';

/**
 * v6.1: Performance Tracker Service
 * Conecta os pontos entre Telemetria ( timestamps ) e Inteligência Logística.
 */

export interface PerformanceSnapshot {
    speedFactor: number;
    avgSpeedKmH: number;
    avgServiceTimeMinutes: number;
    sampleSize: number;
    serviceTimeLearning: ServiceTimeResolution;
}

const SERVICE_TIME_MINUTES_FALLBACK = 5;
const SERVICE_TIME_CONFIDENCE_THRESHOLD = 0.65;

const roundToOneDecimal = (value: number): number => Number(value.toFixed(1));

const resolveServiceTimeLearning = (
    completedOrders: Order[],
    currentDriver: Driver
): ServiceTimeResolution => {
    const metrics = calculateServiceTimeMetrics(completedOrders, { driverId: currentDriver.id });
    const persistedDriverValue = currentDriver.avgServiceTimeMinutes;

    if (metrics.confidence >= SERVICE_TIME_CONFIDENCE_THRESHOLD && !metrics.isStale) {
        const learnedMinutes = metrics.last30Days > 0 ? metrics.last30Days : metrics.history;
        return {
            minutes: roundToOneDecimal(learnedMinutes),
            source: 'driver_real_data',
            confidence: metrics.confidence,
            sampleCount: metrics.sampleCount,
            lastObservationAt: metrics.lastObservationAt,
            rationale: 'Driver observations are recent and above confidence threshold.'
        };
    }

    if (persistedDriverValue && persistedDriverValue > 0) {
        return {
            minutes: roundToOneDecimal(persistedDriverValue),
            source: 'driver_profile_fallback',
            confidence: Math.max(0.4, metrics.confidence),
            sampleCount: metrics.sampleCount,
            lastObservationAt: metrics.lastObservationAt,
            rationale: 'Using persisted driver profile while fresh observations remain sparse or stale.'
        };
    }

    return {
        minutes: SERVICE_TIME_MINUTES_FALLBACK,
        source: 'profile_default_fallback',
        confidence: 0,
        sampleCount: metrics.sampleCount,
        lastObservationAt: metrics.lastObservationAt,
        rationale: 'No sufficient driver history available yet.'
    };
};

/**
 * Calcula a performance baseada no histórico recente.
 * Ignora amostras inconsistentes (faltando timestamps ou tempos negativos).
 */
export const calculateDriverPerformance = (
    completedOrders: Order[],
    currentDriver: Driver
): PerformanceSnapshot => {
    const serviceLearning = resolveServiceTimeLearning(completedOrders, currentDriver);

    // 1. Filtra ordens válidas do motorista específico com telemetria completa
    const validSamples = completedOrders.filter(o =>
        resolveServiceLearningDriverId(o) === currentDriver.id &&
        o.departedHubAt &&
        o.arrivedAt &&
        o.deliveredAt &&
        o.arrivedAt > o.departedHubAt &&
        o.deliveredAt > o.arrivedAt
    );

    if (validSamples.length === 0) {
        return {
            speedFactor: currentDriver.speedFactor || 1.0,
            avgSpeedKmH: currentDriver.avgSpeedKmH || 20.0,
            avgServiceTimeMinutes: serviceLearning.minutes,
            sampleSize: 0,
            serviceTimeLearning: serviceLearning
        };
    }

    let totalSpeedFactor = 0;
    let totalServiceTime = 0;
    let totalSpeedKmH = 0;

    validSamples.forEach(order => {
        const travelTimeMins = (order.arrivedAt! - order.departedHubAt!) / 60000;
        const distance = order.distance || 1;

        const baselineMins = distance * 3; // 20km/h baseline
        const legSpeedFactor = baselineMins / Math.max(0.5, travelTimeMins);

        totalSpeedFactor += Math.max(0.5, Math.min(2.0, legSpeedFactor));
        totalSpeedKmH += (distance / (Math.max(0.5, travelTimeMins) / 60));

        const serviceMins = (order.deliveredAt! - order.arrivedAt!) / 60000;
        totalServiceTime += serviceMins;
    });

    const avgSpeedFactor = totalSpeedFactor / validSamples.length;
    const avgServiceTime = totalServiceTime / validSamples.length;
    const avgSpeedKmH = totalSpeedKmH / validSamples.length;

    // v6.1: Suavização (Exponential Moving Average simplificada)
    const finalSpeedFactor = currentDriver.speedFactor
        ? (avgSpeedFactor * 0.7) + (currentDriver.speedFactor * 0.3)
        : avgSpeedFactor;

    const finalServiceTime = (avgServiceTime * 0.7) + (serviceLearning.minutes * 0.3);

    const finalSpeedKmH = currentDriver.avgSpeedKmH
        ? (avgSpeedKmH * 0.7) + (currentDriver.avgSpeedKmH * 0.3)
        : avgSpeedKmH;

    return {
        speedFactor: Number(finalSpeedFactor.toFixed(2)),
        avgSpeedKmH: Number(finalSpeedKmH.toFixed(1)),
        avgServiceTimeMinutes: Number(finalServiceTime.toFixed(1)),
        sampleSize: validSamples.length,
        serviceTimeLearning: {
            ...serviceLearning,
            minutes: Number(finalServiceTime.toFixed(1)),
            rationale: `${serviceLearning.rationale} Continuous blend applied with latest route telemetry.`
        }
    };
};
